import { Link } from "react-router-dom";
import { ArrowRight, Music, Flame, Users, Heart } from "lucide-react";
import { Button } from "../components/ui/button";

const HERO = "https://images.unsplash.com/photo-1591522810850-58128c5fb089?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const GALLERY = [
  { url: "https://images.unsplash.com/photo-1464207687429-7505649dae38?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", caption: "Youth Hike & Prayer" },
  { url: "https://images.unsplash.com/photo-1478131143081-80f7f84ca84d?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", caption: "Camp 2025 — Life Reset" },
  { url: "https://images.unsplash.com/photo-1555992336-03a23c7b20ee?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", caption: "Youth Braai" },
  { url: "https://images.unsplash.com/photo-1593113598332-cd288d649433?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", caption: "Community Outreach" },
  { url: "https://images.unsplash.com/photo-1485846234645-a62644f84728?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", caption: "Christian Movie Night" },
  { url: "https://images.unsplash.com/photo-1529070538774-1843cb3265df?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", caption: "Youth Bible Study" },
];

export default function Youth() {
  return (
    <div data-testid="youth-page">
      {/* HERO */}
      <section className="relative bg-olive text-bone overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="ETI Youth" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-olive via-olive/70 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <p className="overline text-terracotta mb-5">ETI Youth</p>
          <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] max-w-4xl">
            The future is <em className="italic font-light text-terracotta">now.</em>
          </h1>
          <p className="mt-8 text-lg sm:text-xl text-bone/85 max-w-2xl leading-relaxed">
            A generation set apart — burning with purpose, marked by the presence of God, and called
            to occupy every sphere of influence for the Kingdom.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/events">
              <Button className="bg-terracotta hover:bg-terracotta-hover text-white rounded-none px-8 h-14 text-base font-semibold" data-testid="youth-events-cta">
                Upcoming Events <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
            <a href="https://www.youtube.com/@ETIMBOMBELA" target="_blank" rel="noreferrer">
              <Button variant="outline" className="border-2 border-bone text-bone bg-transparent hover:bg-bone hover:text-olive rounded-none px-8 h-14 text-base font-semibold">
                Watch Online
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* WHEN */}
      <section className="bg-cream border-y border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid md:grid-cols-3 gap-8 text-center md:text-left">
          <div>
            <p className="overline text-terracotta mb-2">Weekly Gathering</p>
            <p className="font-heading font-black text-3xl text-olive">Friday · 17:00</p>
            <p className="text-muted_ink mt-1">Youth Gathering · Kanyamazane HQ</p>
          </div>
          <div>
            <p className="overline text-terracotta mb-2">Ages</p>
            <p className="font-heading font-black text-3xl text-olive">13 – 30</p>
            <p className="text-muted_ink mt-1">Teens, Tweens & Young Adults</p>
          </div>
          <div>
            <p className="overline text-terracotta mb-2">Our Heart</p>
            <p className="font-heading font-black text-3xl text-olive">Occupy.</p>
            <p className="text-muted_ink mt-1">Raise sons and daughters who carry the Kingdom.</p>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <p className="overline text-terracotta mb-3">Our focus</p>
        <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter text-olive max-w-3xl">
          Worship. Word. <em className="italic font-light text-terracotta">Witness.</em>
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {[
            { icon: Music, title: "Worship", text: "Real encounters, loud praise, deep presence." },
            { icon: Flame, title: "Word", text: "Rooted in Scripture and equipped for life." },
            { icon: Heart, title: "Witness", text: "Living the Gospel on campuses and communities." },
            { icon: Users, title: "Fellowship", text: "Doing life together — friends who fight for you." },
          ].map((p) => (
            <div key={p.title} className="bg-white border border-border p-7 hover:border-olive transition-colors">
              <p.icon size={22} className="text-olive" />
              <h3 className="font-heading font-bold text-xl text-ink mt-6">{p.title}</h3>
              <p className="text-sm text-muted_ink mt-2 leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* GALLERY */}
      <section className="bg-olive text-bone">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <p className="overline text-terracotta mb-3">Past Events</p>
              <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">Our story, in pictures.</h2>
            </div>
            <Link to="/join-team" className="text-bone font-semibold hover:text-terracotta flex items-center gap-2">
              Get involved <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-12 gap-4">
            {GALLERY.map((g, i) => {
              const span = i === 0 ? "col-span-12 md:col-span-8" : i === 1 ? "col-span-12 md:col-span-4" : "col-span-6 md:col-span-4";
              return (
                <figure key={i} className={`${span} relative group overflow-hidden aspect-[4/3]`} data-testid={`youth-gallery-${i}`}>
                  <img src={g.url} alt={g.caption} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4 text-sm font-semibold tracking-wide">
                    {g.caption}
                  </figcaption>
                </figure>
              );
            })}
          </div>
          <p className="mt-8 text-xs text-bone/60 italic">Images are placeholders — upload your own via the admin dashboard as events take place.</p>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-20 text-center">
        <p className="overline text-terracotta mb-4">You belong here</p>
        <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter text-olive leading-[1.05]">
          Come just as you are. <em className="italic font-light text-terracotta">Leave lit up.</em>
        </h2>
        <div className="mt-10 flex flex-wrap gap-4 justify-center">
          <Link to="/join-team">
            <Button className="bg-terracotta hover:bg-terracotta-hover text-white rounded-none px-8 h-14 text-base font-semibold">
              Serve on a Youth Team
            </Button>
          </Link>
          <Link to="/homecell">
            <Button variant="outline" className="border-2 border-olive text-olive rounded-none px-8 h-14 text-base font-semibold">
              Join a Homecell
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
